import { useCallback, useMemo, useState } from "react";
import { AnimatePresence } from "motion/react";
import { twMerge } from "tailwind-merge";
import { Loader } from "@themes/pollinator/ui/Loader";
import { MotionFade } from "@themes/pollinator/ui/MotionFade";
import {
  ContiguousTimeslot,
  Timeslot,
  groupTimeslotsByTime,
} from "@core/features/timeslots";
import type { SchedulingMode } from "@core/features/actions";
import type { ProcessedSelectableOption } from "@core/features/scheduling";
import { SchedulingAvailabilityGridHeaderRow } from "../SchedulingAvailabilityGridHeaderRow";
import { SchedulingAvailabilityGridTimeRow } from "../SchedulingAvailabilityGridTimeRow";

export type Props = {
  className?: string;
  isLoading: boolean;
  timeSlots: Timeslot[];
  selectedTimeSlots: Timeslot[];
  contiguousTimeslots: ContiguousTimeslot[];
  handleMouseDown: (e: React.MouseEvent, slot: Timeslot) => void;
  handleMouseOver: (e: React.MouseEvent, slot: Timeslot) => void;
  handleTouchStart: (e: React.TouchEvent, slot: Timeslot) => void;
  handleTouchMove: (e: React.TouchEvent, slot: Timeslot) => void;
  handleTouchEnd: (e: React.TouchEvent, slot: Timeslot) => void;
  /** Array of pre-formatted header strings with timezone abbreviation (e.g., "Jan 14 PST") */
  headers: string[];
  /** Number of columns to render */
  columnCount: number;
  /** Selectable options for 'select-options' mode (processed with required id/day) */
  selectableOptions: ProcessedSelectableOption[];
  /** IDs of currently selected options */
  selectedOptionIds: string[];
  /** Whether we're in an options-based mode (select-options or repeating) */
  isOptionsMode: boolean;
  /** The scheduling mode */
  mode: SchedulingMode;
};

export function SchedulingAvailabilityGrid({
  isLoading,
  timeSlots,
  selectedTimeSlots,
  contiguousTimeslots,
  handleMouseDown,
  handleMouseOver,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  headers,
  columnCount,
  selectableOptions,
  selectedOptionIds,
  isOptionsMode,
  mode,
}: Props) {
  // Track hovered option IDs for group hover effect
  // In repeating mode, this will contain all option IDs with the same time range
  const [hoveredOptionIds, setHoveredOptionIds] = useState<Set<string>>(new Set());
  // Track focused option IDs for group focus effect (keyboard navigation)
  const [focusedOptionIds, setFocusedOptionIds] = useState<Set<string>>(new Set());

  // Build a map from option ID to all options with the same time range (for repeating mode)
  const repeatingGroupMap = useMemo(() => {
    if (mode !== "repeating") return new Map<string, string[]>();
    
    const map = new Map<string, string[]>();
    for (const option of selectableOptions) {
      // Find all options with the same time range
      const sameTimeOptions = selectableOptions.filter(
        (opt) =>
          opt.startHour === option.startHour &&
          opt.startMinute === option.startMinute &&
          opt.endHour === option.endHour &&
          opt.endMinute === option.endMinute
      );
      
      map.set(option.id, sameTimeOptions.map((opt) => opt.id));
    }
    return map;
  }, [selectableOptions, mode]);

  // Handle option hover - in repeating mode, highlight all options with same time range
  const handleOptionHover = useCallback(
    (optionId: string | null) => {
      if (optionId === null) {
        setHoveredOptionIds(new Set());
        return;
      }

      if (mode === "repeating") {
        // Get all option IDs with the same time range
        const groupIds = repeatingGroupMap.get(optionId) || [optionId];
        setHoveredOptionIds(new Set(groupIds));
      } else {
        // In select-options mode, just highlight the single option
        setHoveredOptionIds(new Set([optionId]));
      }
    },
    [mode, repeatingGroupMap]
  );

  // Handle option focus - same logic as hover for group highlighting
  const handleOptionFocus = useCallback(
    (optionId: string | null) => {
      if (optionId === null) {
        setFocusedOptionIds(new Set());
        return;
      }

      if (mode === "repeating") {
        // Get all option IDs with the same time range
        const groupIds = repeatingGroupMap.get(optionId) || [optionId];
        setFocusedOptionIds(new Set(groupIds));
      } else {
        // In select-options mode, just highlight the single option
        setFocusedOptionIds(new Set([optionId]));
      }
    },
    [mode, repeatingGroupMap]
  );

  // Compute which option IDs should be tabbable (for keyboard navigation)
  // In select-options mode: all options are tabbable
  // In repeating mode: only the first option of each repeating group is tabbable
  const tabbableOptionIds = useMemo(() => {
    if (!isOptionsMode) return new Set<string>(); // Freeform mode - handled differently

    if (mode === "repeating") {
      // Only the first option in each repeating group (by day order) is tabbable
      const seenTimeRanges = new Set<string>();
      const tabbable = new Set<string>();

      // Sort options by day to ensure consistent ordering
      const sortedOptions = [...selectableOptions].sort((a, b) => a.day - b.day);

      for (const option of sortedOptions) {
        const timeKey = `${option.startHour}:${option.startMinute}-${option.endHour}:${option.endMinute}`;
        if (!seenTimeRanges.has(timeKey)) {
          seenTimeRanges.add(timeKey);
          tabbable.add(option.id);
        }
      }
      return tabbable;
    }

    // In select-options mode, all options are tabbable
    return new Set(selectableOptions.map((opt) => opt.id));
  }, [isOptionsMode, mode, selectableOptions]);

  // Group time slots by time for rendering grid rows
  const timeSlotsByTime = useMemo(
    () => groupTimeslotsByTime(timeSlots),
    [timeSlots]
  );

  // Dynamic grid columns based on columnCount - use inline style since Tailwind can't handle dynamic values
  const gridColumnsStyle = useMemo(() => {
    // First column is fixed at 48px for time labels, rest are flexible
    return {
      gridTemplateColumns: `48px repeat(${columnCount}, minmax(40px, 1fr))`,
    };
  }, [columnCount]);

  return (
    <div className="relative overflow-x-auto w-full">
      <div className="min-h-[600px] relative">
        <AnimatePresence mode="wait">
          {isLoading ? (
            <MotionFade
              className="flex justify-center items-center h-full w-full col-span-full row-span-full"
              key="loader"
              layout
            >
              <Loader />
            </MotionFade>
          ) : (
            <MotionFade key="grid" layout>
              <div
                className={twMerge(
                  "availability-grid min-w-[600px] grid",
                  // Add 16px column gap in repeating mode
                  mode === "repeating" && "gap-x-4"
                )}
                style={gridColumnsStyle}
                role="grid"
                aria-label="Availability selection grid"
                aria-multiselectable="true"
              >
                <SchedulingAvailabilityGridHeaderRow headers={headers} />

                {timeSlotsByTime.map(([time, slots], idx) => {
                  const isLastRow = idx === timeSlotsByTime.length - 1;
                  return (
                    <div key={idx} className="contents">
                      <SchedulingAvailabilityGridTimeRow
                        formattedTime={time}
                        timeSlots={slots}
                        selectedTimeSlots={selectedTimeSlots}
                        contiguousTimeslots={contiguousTimeslots}
                        handleMouseDown={handleMouseDown}
                        handleMouseOver={handleMouseOver}
                        handleTouchStart={handleTouchStart}
                        handleTouchMove={handleTouchMove}
                        handleTouchEnd={handleTouchEnd}
                        timeIndex={idx}
                        selectableOptions={selectableOptions}
                        selectedOptionIds={selectedOptionIds}
                        isOptionsMode={isOptionsMode}
                        onOptionHover={handleOptionHover}
                        hoveredOptionIds={hoveredOptionIds}
                        onOptionFocus={handleOptionFocus}
                        focusedOptionIds={focusedOptionIds}
                        isRepeatingMode={mode === "repeating"}
                        isLastRow={isLastRow}
                        tabbableOptionIds={tabbableOptionIds}
                      />
                    </div>
                  );
                })}
              </div>
            </MotionFade>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default SchedulingAvailabilityGrid;

